import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { useLocation } from 'react-router-dom';

import { toast } from 'react-toastify';
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Form,
  Collapse
} from 'reactstrap';
import { useForm, Controller } from 'react-hook-form';
import SimpleHeader from 'components/Headers/SimpleHeader.js';
import Loader from 'components/Loader';

import ListComments from '../Comments/CommentsList';
import CommentModal from '../Comments/CommentsForm';
import ModalComment from '../Comments/ModalUpdateComment';
import { FiPlus } from 'react-icons/fi';

import AccountService from '../../services/accountService';
import { toBrazilianFullDate } from 'views/CRM/shared/utils/date';
import AccountInputs from '../FormInputs';
import AccountContacts from './AccountContacts';
import AccountOpportunities from './AccountOpportunities';
import AddContactModal from './AddContactModal';
import AddOpportunityModal from './AddOpportunityModal';
import AccountProtocols from './AccountProtocols';
import FlexSelect from 'components/Inputs/FlexSelect';
import { useListOpportunityStagesByRecordType } from 'views/CRM/OpportunityStages/hooks/useListOpportunityStagesByRecordType';
import { useUsers } from 'views/Report/Protocol/hooks/useUsers';
import Datepicker from 'components/Inputs/DatePicker';
import { AiOutlineDown, AiOutlineRight } from 'react-icons/ai';
import ReactButton from 'components/Buttons/ReactButton';
import { useRecordTypesByUser } from 'views/RecordType/hooks/useRecordTypesByUser';
import { entitiesName } from 'views/CRM/shared/utils/entitiesName';
import AccountAddress from './AccountAddress';
import AddAddressModal from 'components/AddAddressModal';
import { RiContactsBookFill, RiUserLocationFill } from 'react-icons/ri';
import { PiArticleFill, PiNewspaperClippingFill } from 'react-icons/pi';

const sectionStyle = {
  backgroundColor: 'rgb(238 239 242)',
  border: '1px solid rgb(233 233 233)',
  padding: '0.8rem 1rem',
  borderRadius: '0.1rem'
};

const AccountDetails = () => {
  const history = useHistory();
  const location = useLocation();
  const { from } = location.state || {};
  const {
    control,
    handleSubmit,

    setValue
  } = useForm();
  const { userList } = useUsers();
  const { recordTypeId, id } = useParams();
  const [account, setAccount] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [listComments, setListComments] = useState([]);

  const [filterOpportunitiesCollapse, setFilterOpportunitiesCollapse] =
    useState(false);
  const [filterProtocolCollapse, setFilterProtocolCollapse] = useState(false);

  const [openModalCreateComment, setOpenModalCreateComment] = useState(false);

  const [dataCommentUpdate, setDataCommentUpdate] = useState([]);
  const [openModalUpdateComment, setOpenModalUpdateComment] = useState(false);
  const [selectedRecordTypeId, setSelectedRecordTypeId] =
    useState(recordTypeId);
  const { recordTypes, isLoadingRecordTypes, getRecordTypesByUser } =
    useRecordTypesByUser(entitiesName.OPPORTUNITY);
  const {
    getOpportunityStagesByRecordType,
    isLoadingOpportunityStages,
    opportunityStages
  } = useListOpportunityStagesByRecordType();

  const handleUpdateComment = (dataComment) => {
    setOpenModalUpdateComment(true);
    setDataCommentUpdate(dataComment);
  };

  const handleCreateComment = () => {
    setOpenModalCreateComment(true);
  };

  const getCommnents = useCallback(() => {
    if (id) {
      AccountService.getAccountsComments(id)
        .then((res) => {
          setListComments(res.data.data);
        })
        .catch((err) => {
          toast.error('Houve um erro ao buscar os comentários dessa conta', {
            closeOnClick: true,
            autoClose: 3000
          });
          console.log(err);
        });
    }
  }, [id]);

  useEffect(getCommnents, [getCommnents]);

  useEffect(() => {
    getOpportunityStagesByRecordType(selectedRecordTypeId);
  }, [selectedRecordTypeId, recordTypeId, getOpportunityStagesByRecordType]);

  useEffect(() => {
    getRecordTypesByUser();
  }, [getRecordTypesByUser]);

  const reloadComments = () => {
    AccountService.getAccountsComments(id).then((res) => {
      setListComments(res.data.data);
    });
  };

  const getAccount = useCallback(() => {
    AccountService.getById(recordTypeId, id)
      .then((response) => {
        setAccount(response.data.data);
      })
      .catch(() => {
        toast.error('Ocorreu um erro ao buscar informações da conta');
        backToAccountListing();
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [backToAccountListing, id, recordTypeId]);

  useEffect(() => {
    getAccount();
  }, [getAccount]);

  const reloadAccount = useCallback(() => {
    getAccount();
  }, [getAccount]);

  const backToAccountListing = useCallback(() => {
    history.push('/admin/crm/accounts', { recordTypeId });
  }, [recordTypeId, history]);

  const onSubmit = async (data) => {
    await AccountService.getById(recordTypeId, id, data)
      .then((response) => {
        setAccount(response.data.data);
      })
      .catch(() => {
        toast.error('Ocorreu um erro ao aplicar os filtros');
      });
  };

  if (isLoading) {
    return (
      <Row className="justify-content-md-center">
        <Loader />
      </Row>
    );
  }

  if (!account) return null;

  return (
    <>
      <SimpleHeader
        name="Account edit"
        parentName="Account"
        returnPath={
          from === 'NotPurchasedReport'
            ? '/admin/crm/report/not-purchased'
            : '/admin/crm/accounts'
        }
        navigatorState={{ recordTypeId }}
      />
      <Container className="configuration-container mt--6" fluid>
        <Card>
          <CardBody>
            <Row className="mb-3">
              <Col sm="3">
                <h3 className="font-weight-bold small mb-0">Criado por:</h3>
                <span>{account.createdByName}</span>
              </Col>
              <Col sm="3">
                <h3 className="font-weight-bold small mb-0">Criado em:</h3>
                <span>{toBrazilianFullDate(account.createdAt)}</span>
              </Col>
              <Col sm="3">
                <h3 className="font-weight-bold small mb-0">
                  Última modificação por:
                </h3>
                <span>{account.lastUpdatedByName}</span>
              </Col>
              <Col sm="3">
                <h3 className="font-weight-bold small mb-0">
                  Última modificação em:
                </h3>
                <span>{toBrazilianFullDate(account.lastUpdatedAt)}</span>
              </Col>
            </Row>

            <hr style={{ margin: '0', paddingTop: '0' }} />

            <div className="mt-3" style={sectionStyle}>
              <h2>Dados da conta</h2>
              <AccountInputs
                recordTypeId={recordTypeId}
                account={account}
                showCallIconOnPhoneField={true}
                showMessageIconOnPhoneField={true}
              />
            </div>

            {listComments && (
              <ListComments
                listComments={listComments}
                reloadComments={reloadComments}
                handleUpdateComment={handleUpdateComment}
              />
            )}
            <Row style={{ paddingTop: '20px' }}>
              <Col className="text-right">
                <Button
                  title="Adicionar novo comentário"
                  color="success"
                  type="button"
                  onClick={() => handleCreateComment()}
                >
                  <FiPlus size={16} /> Adicionar Comentário
                </Button>
              </Col>
            </Row>

            <CommentModal
              render={getCommnents}
              accountId={id}
              isModalOpen={openModalCreateComment}
              onModalToggle={setOpenModalCreateComment}
              unmountOnClose={true}
            />

            <ModalComment
              render={getCommnents}
              dataComment={dataCommentUpdate}
              isModalOpen={openModalUpdateComment}
              onModalToggle={setOpenModalUpdateComment}
              unmountOnClose={true}
            />

            <div className="mt-3" style={sectionStyle}>
              <div className="d-flex justify-content-between mb-2">
                <h2>
                  <RiContactsBookFill /> Contatos (
                  {account?.contacts?.length || 0})
                </h2>
                <AddContactModal
                  accountId={id}
                  onAddedContact={reloadAccount}
                  buttonOpenModal={({ onClick }) => (
                    <Button size="sm" onClick={onClick}>
                      Adicionar
                    </Button>
                  )}
                />
              </div>

              <AccountContacts
                contacts={account.contacts}
                accountId={id}
                reloadAccount={reloadAccount}
              />
            </div>

            <div className="mt-3" style={sectionStyle}>
              <div className="d-flex justify-content-between mb-2">
                <h2>
                  <RiUserLocationFill /> Endereços (
                  {account?.addresses?.length || 0})
                </h2>
                <AddAddressModal
                  entityType="account"
                  entityId={id}
                  entity={account}
                  onAddedAddress={reloadAccount}
                  buttonOpenModal={({ onClick }) => (
                    <Button size="sm" onClick={onClick}>
                      Adicionar
                    </Button>
                  )}
                />
              </div>

              <AccountAddress
                addresses={account.addresses}
                accountId={id}
                reloadAccount={reloadAccount}
              />
            </div>

            <div className="mt-3" style={sectionStyle}>
              <div className="d-flex justify-content-between mb-2">
                <h2>
                  <PiArticleFill /> Oportunidades (
                  {account?.opportunities?.length || 0})
                </h2>
                <AddOpportunityModal
                  accountId={id}
                  onAddedOpportunity={reloadAccount}
                  buttonOpenModal={({ onClick }) => (
                    <Button size="sm" onClick={onClick}>
                      Adicionar
                    </Button>
                  )}
                />
              </div>
              <div className="mb-3">
                <Button
                  color="link"
                  onClick={() =>
                    setFilterOpportunitiesCollapse(!filterOpportunitiesCollapse)
                  }
                  style={{
                    textAlign: 'left',
                    fontWeight: 'bold',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  Filtros
                  {filterOpportunitiesCollapse ? (
                    <AiOutlineDown size={16} />
                  ) : (
                    <AiOutlineRight size={16} />
                  )}
                </Button>
                <Collapse isOpen={filterOpportunitiesCollapse}>
                  <div>
                    <Form
                      className="needs-validation"
                      onSubmit={handleSubmit(onSubmit)}
                    >
                      <Row className="mb-3">
                        <Col md="3">
                          <label className="form-control-label">
                            Tipo de Registro
                          </label>
                          <Controller
                            render={({ value }) => (
                              <FlexSelect
                                dataOptions={recordTypes || []}
                                isMulti={false}
                                value={value}
                                isClearable={true}
                                valueController={(field, val) => {
                                  setValue(field, val);
                                  setSelectedRecordTypeId(val);
                                }}
                                fieldName="recordTypeId"
                                labelName="name"
                                valueName="id"
                                disabled={isLoadingRecordTypes}
                                closeMenuOnSelect={true}
                              />
                            )}
                            control={control}
                            defaultValue={selectedRecordTypeId}
                            name="recordTypeId"
                          />
                        </Col>
                        <Col md="3">
                          <label className="form-control-label">
                            Status da Oportunidade
                          </label>
                          <Controller
                            render={(props) => (
                              <FlexSelect
                                dataOptions={opportunityStages || []}
                                isMulti={false}
                                value={props.value}
                                valueController={setValue}
                                fieldName="stageId"
                                labelName="name"
                                valueName="id"
                                isClearable={true}
                                disabled={isLoadingOpportunityStages}
                                closeMenuOnSelect={true}
                              />
                            )}
                            control={control}
                            defaultValue={''}
                            name="stageId"
                          />
                        </Col>
                        <Col sm="3">
                          <label className="form-control-label">
                            Responsável
                          </label>
                          <Controller
                            render={(props) => (
                              <FlexSelect
                                dataOptions={userList || []}
                                isMulti={false}
                                value={props.value}
                                valueController={setValue}
                                fieldName="responsibleLogin"
                                labelName="name"
                                valueName="login"
                                isClearable={true}
                                closeMenuOnSelect={true}
                              />
                            )}
                            control={control}
                            name="responsibleLogin"
                            defaultValue=""
                          />
                        </Col>
                        <Col sm="3">
                          <label className="form-control-label">
                            Data de criação
                          </label>
                          <Controller
                            render={(props) => (
                              <Datepicker
                                fieldName="createdAt"
                                valueController={setValue}
                                defaultValue={props.value}
                              />
                            )}
                            control={control}
                            name="createdAt"
                          />
                        </Col>
                      </Row>
                      <ReactButton btnColor="primary" type="submit">
                        Pesquisar
                      </ReactButton>
                    </Form>
                  </div>
                </Collapse>
              </div>

              <AccountOpportunities
                opportunities={account?.opportunities}
                reloadAccount={reloadAccount}
              />
            </div>

            <div className="mt-3" style={sectionStyle}>
              <h2>
                <PiNewspaperClippingFill />{' '}
                {`Protocolos (${account?.protocols?.length || 0})`}
              </h2>
              <div className="mb-3">
                <Button
                  color="link"
                  onClick={() =>
                    setFilterProtocolCollapse(!filterProtocolCollapse)
                  }
                  style={{
                    textAlign: 'left',
                    fontWeight: 'bold',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  Filtros
                  {filterProtocolCollapse ? (
                    <AiOutlineDown size={16} />
                  ) : (
                    <AiOutlineRight size={16} />
                  )}
                </Button>
                <Collapse isOpen={filterProtocolCollapse}>
                  <div>
                    <Form
                      className="needs-validation"
                      onSubmit={handleSubmit(onSubmit)}
                    >
                      <Row className="mb-3">
                        <Col sm="3">
                          <label className="form-control-label">
                            Responsável
                          </label>
                          <Controller
                            render={(props) => (
                              <FlexSelect
                                dataOptions={userList || []}
                                isMulti={false}
                                value={props.value}
                                valueController={setValue}
                                fieldName="responsibleLoginProtocol"
                                labelName="name"
                                valueName="login"
                                isClearable={true}
                                closeMenuOnSelect={true}
                              />
                            )}
                            control={control}
                            name="responsibleLoginProtocol"
                            defaultValue=""
                          />
                        </Col>
                        <Col sm="3">
                          <label className="form-control-label">
                            Data de criação
                          </label>
                          <Controller
                            render={(props) => (
                              <Datepicker
                                fieldName="createdAtProtocol"
                                valueController={setValue}
                                defaultValue={props.value}
                              />
                            )}
                            control={control}
                            name="createdAtProtocol"
                          />
                        </Col>
                        <ReactButton
                          btnColor="primary"
                          style={{
                            ...(ReactButton.defaultProps?.style || {}),
                            marginTop: '2rem'
                          }}
                          type="submit"
                        >
                          Pesquisar
                        </ReactButton>
                      </Row>
                    </Form>
                  </div>
                </Collapse>
              </div>

              <AccountProtocols protocols={account?.protocols} />
            </div>
          </CardBody>
        </Card>
      </Container>
    </>
  );
};

export default AccountDetails;
